import { apiClient } from '@/lib/api-client';
import {
  connectBitbucket,
  disconnectBitbucket,
  getBitbucketImportedUsers,
  getBitbucketStatus,
  getBitbucketSyncStatus,
  syncBitbucket,
  updateBitbucketToken,
} from '@kirin/api-client/api/bitbucket';
import type {
  BitbucketConnectRequest,
  BitbucketConnectResult,
  BitbucketDisconnectResult,
  BitbucketImportedUsersResult,
  BitbucketStatusResponse,
  BitbucketSyncResult,
  BitbucketSyncStatusResponse,
  BitbucketUpdateTokenRequest,
  BitbucketUpdateTokenResult,
} from '@kirin/api-client/contracts/bitbucket';
import { useEffect, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { queryKeys } from './queryKeys';

const bitbucketQueryKeys = {
  status: (orgId: string): readonly string[] => ['bitbucket-status', orgId],
  syncStatus: (orgId: string): readonly string[] => [
    'bitbucket-sync-status',
    orgId,
  ],
  importedUsers: (orgId: string): readonly string[] => [
    'bitbucket-imported-users',
    orgId,
  ],
} as const;

function invalidateAllBitbucketQueries(
  queryClient: QueryClient,
  organizationId: string
): void {
  queryClient.invalidateQueries({
    queryKey: bitbucketQueryKeys.status(organizationId),
  });
  queryClient.invalidateQueries({
    queryKey: bitbucketQueryKeys.syncStatus(organizationId),
  });
  queryClient.invalidateQueries({
    queryKey: bitbucketQueryKeys.importedUsers(organizationId),
  });
}

export function useBitbucketStatus(
  organizationId: string
): UseQueryResult<BitbucketStatusResponse> {
  return useQuery({
    queryKey: bitbucketQueryKeys.status(organizationId),
    queryFn: async (): Promise<BitbucketStatusResponse> => {
      return getBitbucketStatus(apiClient, organizationId);
    },
    enabled: !!organizationId,
  });
}

export function useBitbucketConnect(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: BitbucketConnectRequest
    ): Promise<BitbucketConnectResult> => {
      return connectBitbucket(apiClient, organizationId, data);
    },
    onSuccess: () => {
      invalidateAllBitbucketQueries(queryClient, organizationId);
    },
  });
}

export function useBitbucketDisconnect(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<BitbucketDisconnectResult> => {
      return disconnectBitbucket(apiClient, organizationId);
    },
    onSuccess: () => {
      invalidateAllBitbucketQueries(queryClient, organizationId);
      queryClient.removeQueries({
        queryKey: bitbucketQueryKeys.importedUsers(organizationId),
      });
    },
  });
}

export function useBitbucketUpdateToken(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: BitbucketUpdateTokenRequest
    ): Promise<BitbucketUpdateTokenResult> => {
      return updateBitbucketToken(apiClient, organizationId, data);
    },
    onSuccess: () => {
      invalidateAllBitbucketQueries(queryClient, organizationId);
    },
  });
}

export function useBitbucketSync(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<BitbucketSyncResult> => {
      return syncBitbucket(apiClient, organizationId);
    },
    onSuccess: () => {
      invalidateAllBitbucketQueries(queryClient, organizationId);
    },
  });
}

export function useBitbucketSyncStatus(
  organizationId: string,
  options?: { enabled?: boolean }
): UseQueryResult<BitbucketSyncStatusResponse> {
  const queryClient = useQueryClient();
  const wasRunningRef = useRef(false);

  const queryResult = useQuery({
    queryKey: bitbucketQueryKeys.syncStatus(organizationId),
    queryFn: async (): Promise<BitbucketSyncStatusResponse> => {
      return getBitbucketSyncStatus(apiClient, organizationId);
    },
    enabled: options?.enabled !== false && !!organizationId,
    refetchInterval: query => {
      const data = query.state.data;
      return data?.isRunning ? 5000 : false;
    },
  });

  useEffect(() => {
    const isRunning = queryResult.data?.isRunning ?? false;
    if (isRunning) {
      wasRunningRef.current = true;
    } else if (wasRunningRef.current) {
      wasRunningRef.current = false;
      invalidateAllBitbucketQueries(queryClient, organizationId);
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.members(organizationId),
      });
    }
  }, [queryResult.data?.isRunning, queryClient, organizationId]);

  return queryResult;
}

export function useBitbucketImportedUsers(
  organizationId: string,
  options?: { enabled?: boolean }
): UseQueryResult<BitbucketImportedUsersResult> {
  return useQuery({
    queryKey: bitbucketQueryKeys.importedUsers(organizationId),
    queryFn: async (): Promise<BitbucketImportedUsersResult> => {
      return getBitbucketImportedUsers(apiClient, organizationId);
    },
    enabled: options?.enabled !== false && !!organizationId,
  });
}
